import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/faqService";
import type { FaqItem } from "../types";

interface FaqState {
  items: FaqItem[];
  loading: boolean;
  error: string | null;
}

const initialState: FaqState = { items: [], loading: false, error: null };

export const fetchFaqs = createAsyncThunk("cms/faq/fetch", api.listFaqs);
export const addFaq = createAsyncThunk("cms/faq/add", api.createFaq);
export const editFaq = createAsyncThunk(
  "cms/faq/edit",
  ({ id, payload }: { id: number; payload: Omit<FaqItem, "id"> }) => api.updateFaq(id, payload)
);
export const removeFaq = createAsyncThunk("cms/faq/remove", async (id: number) => {
  await api.deleteFaq(id);
  return id;
});

const faqSlice = createSlice({
  name: "cmsFaq",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFaqs.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFaqs.fulfilled, (state, action: PayloadAction<FaqItem[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load FAQs.";
      })
      .addCase(addFaq.fulfilled, (state, action: PayloadAction<FaqItem>) => {
        state.items.push(action.payload);
      })
      .addCase(editFaq.fulfilled, (state, action: PayloadAction<FaqItem>) => {
        const idx = state.items.findIndex((i) => i.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(removeFaq.fulfilled, (state, action: PayloadAction<number>) => {
        state.items = state.items.filter((i) => i.id !== action.payload);
      });
  },
});

export default faqSlice.reducer;